import { withCompInfo } from '@wix/editor-elements-integrations'
import type { ContainerCSS, Properties, VideoBoxCss, VideoBoxDefinition } from './VideoBox'
import type {
	BackgroundMedia,
	BaseDataItem,
	BoxShadowDefinition,
	ParsedCssStyle,
	Video,
} from '@wix/thunderbolt-becky-types'
import { getVideoMediaRef } from '../../core/mappers/Media/Media.utils'
import { getMaskCSSVars } from '../WPhoto/WPhoto.mapper'
import { parseCss } from '../../core/schemas/css/Css.utils'
import { fixBorderHairline } from '@wix/thunderbolt-becky-root'
import { color, ParsedSvg } from '@wix/thunderbolt-commons'
import { assign, kebabCase, mapKeys, pickBy } from 'lodash'
import { getFillLayersCSSVars } from '../../core/mappers/FillLayers/FillLayers.utils'

type VideoWithSvg = Video & { resolvedSvg?: ParsedSvg }

const flipTransformMap: Record<string, string> = {
	vertical: 'scale(1, -1)',
	horizontal: 'scale(-1, 1)',
	both: 'scale(-1, -1)',
	none: 'rotate(0.01deg)',
}

const getDropShadowCSSVars = ({ boxShadow }: { boxShadow: BoxShadowDefinition }) => {
	const shadowColor = boxShadow?.color
	const xValue = boxShadow?.offsetX?.value
	const xUnit = boxShadow?.offsetX?.unit
	const yValue = boxShadow?.offsetY?.value
	const yUnit = boxShadow?.offsetY?.unit
	const blurValue = boxShadow?.blurRadius?.value
	const blurUnit = boxShadow?.blurRadius?.unit
	const rgba = shadowColor && color.getColorFromCssStyle(shadowColor)

	return (
		(rgba && {
			'--fill-container-drop-shadow': `drop-shadow(${xValue}${xUnit} ${yValue}${yUnit} ${blurValue}${blurUnit} ${rgba})`,
			'--box-shadow': 'none', // overwrite simple box-shadow to none
		}) ||
		{}
	)
}

type VideoBoxWithResolvedSvg = BaseDataItem & { resolvedSvg?: ParsedSvg; svgId?: string }
type VideoBoxPropertiesWithOverrides = BaseDataItem &
	Properties & { overrideMask?: VideoBoxWithResolvedSvg; resolvedSvg?: ParsedSvg }

export const css = withCompInfo<VideoBoxCss | {}, VideoBoxDefinition>()(
	[
		'compProps',
		'compDesign',
		'compLayout',
		'hasResponsiveLayout',
		'isMobileView',
		'compId',
		'experiments',
		'siteColors',
		'viewMode',
		// @ts-ignore - Remove once animations is added to the compInfo
		'animations',
		// @ts-ignore - Remove once isNonAdaptiveMobile is added to the compInfo
		'isNonAdaptiveMobile',
		// @ts-ignore - Remove once isOneDocMigrated is added to the compInfo
		'isOneDocMigrated',
		// @ts-ignore - Remove once classicRotation is added to the compInfo
		'classicRotation',
		// @ts-ignore - Remove once classicLayout is added to the compInfo
	],
	({
		compProps,
		compDesign,
		compLayout,
		hasResponsiveLayout,
		// @ts-ignore - Remove once animations is added to the compInfo
		animations,
		isMobileView,
		compId,
		experiments,
		siteColors,
		viewMode,
		// @ts-ignore - Remove once isNonAdaptiveMobile is added to the compInfo
		isNonAdaptiveMobile,
		// @ts-ignore - Remove once isOneDocMigrated is added to the compInfo
		isOneDocMigrated,
		// @ts-ignore - Remove once classicRotation is added to the compInfo
		classicRotation,
	}) => {
		const videoBoxProps = compProps as VideoBoxPropertiesWithOverrides
		const layout = compLayout
		const rotation = isOneDocMigrated ? classicRotation : layout?.rotationInDegrees
		const backgroundMedia = compDesign?.background || ({} as BackgroundMedia)
		const flip = videoBoxProps?.flip || ('none' as string)
		const overrideMask = videoBoxProps?.overrideMask
		const media = getVideoMediaRef(backgroundMedia) as VideoWithSvg
		const svgId = media?.mask?.svgId || overrideMask?.svgId
		const resolvedSvg = media?.resolvedSvg || videoBoxProps?.resolvedSvg

		/*
		 * Hats off to Avital Santo
		 */

		const { maskCSSVars, hasMask } = getMaskCSSVars(svgId, resolvedSvg, 'none', overrideMask)

		const parsedCSSStyle = parseCss(compDesign?.cssStyle || {})

		const hasBorderRadius = !!(!hasMask && parsedCSSStyle?.borderRadius)
		const hasBorderWidth = !!(!hasMask && parsedCSSStyle?.borderWidth)
		const hasBoxShadow = !!parsedCSSStyle?.boxShadow
		const hasDropShadow = hasMask && hasBoxShadow // TODO: need SVG filter fallback for Chrome (layout breaks when combined with animations using clip/clip-path)

		// If we have both borderRadius and actual borders and/or shadow
		// adding translateZ(0) creates an ugly hairline (נימה) in the perimeter on Webkit and friends
		// so we paint over it with the same color as the border/shadow to hide it
		// if we have drop-shadow, currently there's no border, and no need for the fix altogether
		const fixBorderHairlineResult = fixBorderHairline(parsedCSSStyle, hasDropShadow)
		const containerDesignCSSVars = mapKeys(
			pickBy(
				fixBorderHairlineResult,
				(_: any, key: keyof ParsedCssStyle) =>
					// remove borders and border-radius if we have mask
					// keep box-shadow => it will be transformed into drop-shadow
					!(
						(key === 'borderRadius' ||
							key === 'borderWidth' ||
							key === 'borderStyle' ||
							key === 'borderColor') &&
						hasMask
					)
			),
			((__: any, key: keyof ParsedCssStyle) => `--${kebabCase(key)}`) as any
		)

		// In Chrome 79 and Safari 13 flip + rotate on media layer causes the container to stack in-front of inline content components
		// Applying a transform, even one that does nothing fixes it.
		// BUT - if we keep 'clip()' on the bg layer we see a white 'nima' - hence keep rotate(0deg) / merged with mask + rotation fix with `rotate(0.01deg)`
		const flipTransform = flipTransformMap[flip] || 'rotate(0.01deg)' // fix Safari loosing overflow: hidden with border-radius when used with mix-blend-mode

		const dropShadowCSSVars = hasDropShadow
			? getDropShadowCSSVars({
					boxShadow: (compDesign?.cssStyle?.cssBoxShadow?.[0] || {}) as BoxShadowDefinition,
			  })
			: {}

		// fix Safari loosing overflow: hidden on video with border-radius
		const borderRadiusOverflowFix = hasBorderRadius ? 'translateZ(0) ' : ''
		const isResponsive = hasResponsiveLayout
		const nonResponsiveStyle = {
			...(!isOneDocMigrated && {
				height: `${layout.height}px`,
			}),
		}

		const containerOverflow = !hasDropShadow && hasBorderRadius ? 'hidden' : 'visible'
		const containerTransform = `${borderRadiusOverflowFix}${
			flipTransform ||
			// chrome has a bug with masks inside rotated elements, setting any rotation other than 0 on the masked element fixes it (#WEED-21308)
			(hasMask && rotation)
				? ''
				: 'rotate(0.01deg)'
		}`

		// const borderCompensateMargin = hasBorderWidth.ternary(
		// 		chain('-').plus(or(containerDesignCSSVars.get('--border-width'), '0px').split(' ').head()),
		// 		'0px'
		// 	)

		const borderCompensateMargin = hasBorderWidth
			? `-${(containerDesignCSSVars['--border-width'] || '0px').split(' ')[0]}`
			: '0px'

		// overlay layer disappear on Chrome when component is flipped
		const bgOverlayTransform = flipTransform ? 'translateZ(0)' : ''

		const containerCSS = assign(
			containerDesignCSSVars,
			{
				'--fill-container-overflow': containerOverflow,
				'--fill-container-transform': containerTransform,
				'--fill-transform': flipTransform,
				'--border-compensate-margin': borderCompensateMargin,
				'--fill-layer-background-overlay-transform': bgOverlayTransform,
			},
			dropShadowCSSVars,
			maskCSSVars,
			isResponsive ? {} : nonResponsiveStyle
		) as ContainerCSS

		const fillLayersCssVars = getFillLayersCSSVars({
			animations,
			backgroundMedia,
			compDesign,
			compProps,
			compId,
			experiments,
			isMobileView,
			isNonAdaptiveMobile,
			siteColors,
			viewMode,
		})
		return assign(fillLayersCssVars, containerCSS) as VideoBoxCss
	}
)
